import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { P } from '#app/components/typography/p.tsx'
import { Strong } from '#app/components/typography/strong.tsx'
import { Ul } from '#app/components/typography/ul.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Ul',
}

const component: componentProps = {
	name: 'Ul',
	shortName: 'Ul',
	fileName: 'ul',
	category: 'typography',
	description: ['An unordered list of items.'],
	features: ['Drop-in replacement for the native <ul> element.', "Opts-in to your design system's typographic styles."],
	parts: [],
	usage: `<Ul>
	<li>
		<P>
			<Strong>I often do this thing where list items have headings.</Strong>
		</P>
		<P>For some reason I think this looks cool which is unfortunate because it's pretty annoying to get the styles right.</P>
		<P>
			I often have two or three paragraphs in these list items, too, so the hard part is getting the spacing between the paragraphs, list item heading, and separate list items to
			all make sense. Pretty tough honestly, you could make a strong argument that you just shouldn't write this way.
		</P>
	</li>
	<li>
		<P>
			<Strong>Since this is a list, I need at least two items.</Strong>
		</P>
		<P>
			I explained what I'm doing already in the previous list item, but a list wouldn't be a list if it only had one item, and we really want this to look realistic. That's why
			I've added this second list item so I actually have something to look at when writing the styles.
		</P>
	</li>
	<li>
		<P>
			<Strong>It's not a bad idea to add a third item either.</Strong>
		</P>
		<P>
			I think it probably would've been fine to just use two items but three is definitely not worse, and since I seem to be having no trouble making up arbitrary things to type,
			I might as well include it.
		</P>
	</li>
</Ul>
<Ul>
	<li>
		Or just a simple grocery list
		<Ul>
			<li>Milk</li>
			<li>
				Cheese
				<Ul>
					<li>Blue cheese</li>
					<li>Feta</li>
				</Ul>
			</li>
		</Ul>
	</li>
	<li>
		or a to-do list
		<Ul>
			<li>Buy milk</li>
			<li>Walk the dog</li>
			<li>Make dinner</li>
		</Ul>
	</li>
	<li>Make all the use-cases look good</li>
</Ul>`,
	styling: `.ul {
	@apply my-fluid-10 pl-fluid-10 list-disc;
}
.ul > li {
	@apply text-muted-600 text-fluid-md my-fluid-2 max-w-prose;

	/* Bullet point */
	&::marker {
		@apply text-muted-400 font-normal;
	}
	/* Nested lists */
	& > .ul:first-child {
		@apply my-0;
	}
	/* Nested paragraphs */
	& > *:first-child {
		@apply mt-fluid-10 mb-fluid-6;
	}
	& > * {
		@apply mt-fluid-6 mb-fluid-6;
	}
	& > *:last-child {
		@apply mt-fluid-6 mb-fluid-10;
	}
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const SpanRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<Ul>
				<li>
					<P>
						<Strong>I often do this thing where list items have headings.</Strong>
					</P>
					<P>For some reason I think this looks cool which is unfortunate because it's pretty annoying to get the styles right.</P>
					<P>
						I often have two or three paragraphs in these list items, too, so the hard part is getting the spacing between the paragraphs, list item heading, and separate list items to
						all make sense. Pretty tough honestly, you could make a strong argument that you just shouldn't write this way.
					</P>
				</li>
				<li>
					<P>
						<Strong>Since this is a list, I need at least two items.</Strong>
					</P>
					<P>
						I explained what I'm doing already in the previous list item, but a list wouldn't be a list if it only had one item, and we really want this to look realistic. That's why
						I've added this second list item so I actually have something to look at when writing the styles.
					</P>
				</li>
				<li>
					<P>
						<Strong>It's not a bad idea to add a third item either.</Strong>
					</P>
					<P>
						I think it probably would've been fine to just use two items but three is definitely not worse, and since I seem to be having no trouble making up arbitrary things to type,
						I might as well include it.
					</P>
				</li>
			</Ul>
			<Ul>
				<li>
					Or just a simple grocery list
					<Ul>
						<li>Milk</li>
						<li>
							Cheese
							<Ul>
								<li>Blue cheese</li>
								<li>Feta</li>
							</Ul>
						</li>
					</Ul>
				</li>
				<li>
					or a to-do list
					<Ul>
						<li>Buy milk</li>
						<li>Walk the dog</li>
						<li>Make dinner</li>
					</Ul>
				</li>
				<li>Make all the use-cases look good</li>
			</Ul>
			<Features features={features} />
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default SpanRoute
